/**
 * Single source of truth for document-upload validation on the frontend.
 *
 * The backend (`/documents/prepare-upload`) is authoritative and rejects in two
 * distinct ways: a disallowed extension throws `FORBIDDEN_FILE_EXTENSION`
 * (`ForbiddenException`), while a content type that does not match the
 * extension throws a `BadRequestException` (`validateMimeType`).
 *
 * This module mirrors the extension half of that contract so the UI rejects
 * up-front with matching wording. It deliberately does NOT pre-reject on the
 * browser-reported MIME: `File.type` is unreliable - Excel saves `.csv` as
 * `text/plain`, Windows reports `.docx` as empty - so guessing from it rejected
 * files the backend would have accepted. Instead `inferMimeType` canonicalises
 * the content type from the extension, so the value we send always matches what
 * the backend expects. The trade-off is deliberate: the backend's MIME
 * `BadRequestException` can no longer fire for uploads from this UI. The
 * extension check remains live on both sides.
 *
 * Allowed types (backend): pdf, xlsx, xls, docx, txt, csv.
 */

// Extension (without dot) -> the MIME type(s) the backend accepts for it.
// The first entry is the canonical type we send for that extension.
export const SUPPORTED_DOCUMENT_TYPES: Record<string, string[]> = {
  pdf: ['application/pdf'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  xls: ['application/vnd.ms-excel'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  txt: ['text/plain'],
  csv: ['text/csv', 'application/csv']
};

export const SUPPORTED_EXTENSIONS = Object.keys(SUPPORTED_DOCUMENT_TYPES);

export const MAX_DOCUMENT_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

/**
 * MIME -> extensions map, for react-dropzone's `accept` option.
 * Inverts SUPPORTED_DOCUMENT_TYPES so each MIME lists its dotted extensions.
 */
export const ACCEPT: Record<string, string[]> = Object.entries(
  SUPPORTED_DOCUMENT_TYPES
).reduce<Record<string, string[]>>((acc, [extension, mimeTypes]) => {
  mimeTypes.forEach((mime) => {
    const dotted = `.${extension}`;
    acc[mime] = acc[mime] ? [...acc[mime], dotted] : [dotted];
  });
  return acc;
}, {});

/**
 * Comma-joined dotted extension string for a raw `<input type="file" accept>`,
 * e.g. `.pdf,.xlsx,.xls,.docx,.txt,.csv`. Distinct from the dropzone MIME map.
 */
export const ACCEPT_ATTRIBUTE = SUPPORTED_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(',');

export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot < 0 || lastDot === fileName.length - 1) return '';
  return fileName.slice(lastDot + 1).toLowerCase();
};

/**
 * Human-readable allowed-types label, e.g. "PDF, XLSX, XLS, DOCX, TXT, CSV".
 */
const allowedTypesLabel = (): string =>
  SUPPORTED_EXTENSIONS.map((extension) => extension.toUpperCase()).join(', ');

export const unsupportedTypeMessage = (): string =>
  `Unsupported file type. Allowed types: ${allowedTypesLabel()}.`;

export const tooLargeMessage = (): string =>
  `File is too large. Maximum size is ${Math.round(
    MAX_DOCUMENT_FILE_SIZE_BYTES / (1024 * 1024)
  )} MB.`;

/**
 * The content type the backend expects for a file, derived from its extension.
 *
 * The browser's `File.type` is ignored on purpose - see the module comment. The
 * extension is what the backend validates against, so the canonical MIME for
 * that extension is always the safe value to send.
 */
export const inferMimeType = (file: File): string => {
  const extension = getFileExtension(file.name);
  return SUPPORTED_DOCUMENT_TYPES[extension]?.[0] ?? 'application/octet-stream';
};

export type FileValidationResult =
  | { valid: true; mimeType: string }
  | {
      valid: false;
      reason: 'unsupported-extension' | 'empty' | 'too-large';
      message: string;
    };

/**
 * Validate a file against the backend contract: extension, then emptiness, then
 * size. MIME is not checked - `inferMimeType` canonicalises it instead, so the
 * content type we send always matches the extension.
 */
export const validateDocumentFile = (file: File): FileValidationResult => {
  const extension = getFileExtension(file.name);

  if (!extension || !SUPPORTED_DOCUMENT_TYPES[extension]) {
    return {
      valid: false,
      reason: 'unsupported-extension',
      message: unsupportedTypeMessage()
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      reason: 'empty',
      message: 'File is empty.'
    };
  }

  if (file.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
    return {
      valid: false,
      reason: 'too-large',
      message: tooLargeMessage()
    };
  }

  return { valid: true, mimeType: inferMimeType(file) };
};
